import React from 'react'
import { Container, Divider, Typography } from '@cleartrip/ct-design-components'
import { PaxCriteria } from '@/types/srp'
import { getPaxDetails } from '@/utils/srp/helper'
import { pluralize } from '@/utils/general/browserHelper'
import { formatCurrency } from '@/utils'

interface PerAdultTooltipProps {
    totalPrice: number
    totalBaseFare: number
    totalTax: number
    paxCriteria: PaxCriteria[]
    amendInsuranceFee?: number
    cancellationInsuranceFee?: number
    travelInsuranceFee?: number
    vdcFee?: number
    discountAmount?: number
}

interface PriceRowProps {
    label: string
    value: number
    alwaysShow?: boolean
}

const PriceRow: React.FC<PriceRowProps> = ({ label, value, alwaysShow = false }) => {
    if (!alwaysShow && value <= 0) return null
    return (
        <Container className="flex flex-between mb-2">
            <Typography variant="P3" color="neutral">
                {label}
            </Typography>
            <Typography variant="P3" color="neutral">
                {formatCurrency(value)}
            </Typography>
        </Container>
    )
}

const PerAdultTooltip: React.FC<PerAdultTooltipProps> = ({
    totalPrice,
    totalBaseFare,
    totalTax,
    paxCriteria,
    amendInsuranceFee = 0,
    cancellationInsuranceFee = 0,
    travelInsuranceFee = 0,
    vdcFee = 0,
    discountAmount = 0
}) => {
    const [adults, children, infants] = getPaxDetails(paxCriteria)

    const paxDisplayValue = [
        pluralize(adults, 'adult'),
        children && pluralize(children, 'child', 'ren'),
        infants && pluralize(infants, 'infant')
    ]
        .filter(Boolean)
        .join(', ')

    const priceRows: PriceRowProps[] = [
        { label: 'Base Price', value: totalBaseFare, alwaysShow: true },
        { label: 'Taxes & fee', value: totalTax, alwaysShow: true },
        { label: 'Amend insurance fee', value: amendInsuranceFee },
        { label: 'Cancellation insurance fee', value: cancellationInsuranceFee },
        { label: 'Travel Insurance fee', value: travelInsuranceFee },
        { label: 'VDC fee', value: vdcFee },
        { label: 'Discount amount', value: discountAmount }
    ]

    return (
        <Container className="p-3" css={{ minWidth: '216px' }}>
            <Container className="flex flex-between">
                <Typography variant="L3" color="neutral">
                    Total price
                </Typography>
                <Typography variant="L3" color="neutral">
                    {formatCurrency(totalPrice)}
                </Typography>
            </Container>

            <Container className="flex flex-between">
                <Typography variant="B4" color="disabled">
                    <Typography variant="P3" color="subheading">
                        {paxDisplayValue}
                    </Typography>
                </Typography>
            </Container>

            <Divider className="my-2" borderStyle="dashed" borderColor="#e6e6e6" />

            {priceRows.map(row => (
                <PriceRow key={row.label} label={row.label} value={row.value} alwaysShow={row.alwaysShow} />
            ))}
        </Container>
    )
}

export default PerAdultTooltip
